import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, SignOut, Buildings, MapPin, ClockCountdown, CarProfile, CheckCircle, XCircle, Trash, Eye, UserCircle, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useIntervalWhenVisible } from "@/lib/useIntervalWhenVisible";
import { LoadErrorBanner } from "@/components/LoadErrorBanner";
import { fmtDate, fmtDateTime } from "@/lib/fmt";
import { downloadPdf } from "@/lib/pdf";
import AgencyRequestForm from "@/components/AgencyRequestForm";
import SDriveContactCard from "@/components/SDriveContactCard";
import MyInvoicesCard from "@/components/MyInvoicesCard";
import PhoneActions from "@/components/PhoneActions";
import NotificationsBell from "@/components/NotificationsBell";
import MobileNavMenu from "@/components/MobileNavMenu";
import PriceEstimateNote from "@/components/PriceEstimateNote";
import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";
import { useBranding } from "@/contexts/BrandingContext";
import BrandLogo from "@/components/BrandLogo";

function Statcard({ count, label }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-lg p-4 flex-1">
      <p className="text-3xl font-light text-white">{count}</p>
      <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

function statusBadgeColor(status) {
  switch (status) {
    case "pending_price":
    case "new":
    case "available":
    case "interested":
      return "bg-amber-500/15 text-amber-400 border-amber-500/30";
    case "priced":
      return "bg-sky-500/15 text-sky-400 border-sky-500/30";
    case "accepted":
    case "converted":
    case "assigned":
    case "er":
    case "sp":
    case "pec":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    case "dep":
    case "completed":
      return "bg-zinc-700/30 text-zinc-300 border-zinc-600";
    case "rejected":
    case "cancelled":
      return "bg-red-500/15 text-red-400 border-red-500/30";
    default:
      return "bg-zinc-700/30 text-zinc-300 border-zinc-600";
  }
}

export default function AgencyDashboard() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigateProfile = () => { window.location.assign("/profile"); };
  const { t, lang, setLang } = useI18n();
  const [tab, setTab] = useState("requests"); // requests | tracking
  const [requests, setRequests] = useState([]);
  const [rides, setRides] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [showRequestReceived, setShowRequestReceived] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, id }
  const [viewRequest, setViewRequest] = useState(null);
  // Phase N1A — separates "fetch failed" from "list legitimately empty".
  const [loadError, setLoadError] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [rr, rd] = await Promise.all([
        api.get("/ride-requests"),
        api.get("/agency/rides"),
      ]);
      setRequests(rr.data || []);
      setRides(rd.data || []);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Phase 1 stability — polling pauses while the page is hidden.
  useIntervalWhenVisible(fetchAll, 10000);

  const acceptRequest = async (id) => {
    try {
      await api.post(`/ride-requests/${id}/accept`);
      toast.success("✓");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const rejectRequest = async (id) => {
    try {
      await api.post(`/ride-requests/${id}/reject`);
      toast.success("✓");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const deleteRequest = async (id) => {
    try {
      await api.delete(`/ride-requests/${id}`);
      toast.success("✓");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pendingCount = requests.filter((r) => r.status === "pending_price").length;
  const pricedCount = requests.filter((r) => r.status === "priced").length;
  const activeCount = rides.filter((r) => !["completed", "cancelled", "archived"].includes(r.status)).length;

  return (
    <div data-testid="agency-dashboard" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-2">
          <h1 className="text-base sm:text-lg font-medium tracking-tight text-white flex items-center gap-2 min-w-0">
            <BrandLogo className="h-7 w-auto shrink-0" />
            <span className="truncate text-zinc-500 font-light hidden sm:inline">— {t("app.subtitle")}</span>
          </h1>

          {/* Desktop / tablet — unchanged */}
          <div className="hidden md:flex items-center gap-1">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="lang-select" className="w-20 sm:w-24 h-9 bg-[#1A1A1A] border-zinc-700 text-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                {SUPPORTED_LANGS.map((l) => (
                  <SelectItem key={l.code} value={l.code} className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="agency-profile-btn" variant="ghost" size="sm" onClick={navigateProfile} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2" title={t("profile.title")}>
              <UserCircle size={18} />
            </Button>
            <NotificationsBell />
            <Button data-testid="agency-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2">
              <SignOut size={18} />
            </Button>
          </div>

          {/* Mobile — Notifications + Profile + Menu */}
          <div className="flex md:hidden items-center gap-1.5">
            <NotificationsBell />
            <Button data-testid="agency-mobile-profile-btn" variant="ghost" size="sm" onClick={navigateProfile} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2 h-10 w-10" title={t("profile.title")}>
              <UserCircle size={20} />
            </Button>
            <MobileNavMenu
              testid="agency-mobile-menu"
              title={branding.companyName}
              items={[
                {
                  render: () => (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Langue</p>
                      <Select value={lang} onValueChange={setLang}>
                        <SelectTrigger data-testid="m-lang-select" className="w-full h-10 bg-[#1A1A1A] border-zinc-700 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                          {SUPPORTED_LANGS.map((l) => (
                            <SelectItem key={l.code} value={l.code} className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                              {l.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ),
                },
                { divider: true, icon: SignOut, label: t("nav.logout") || "Déconnexion", tone: "danger", onClick: logout, testid: "m-agency-logout" },
              ]}
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Welcome */}
        <div data-testid="agency-welcome" className="mb-8 p-6 rounded-lg border border-zinc-800 bg-gradient-to-br from-[#0f0f0f] to-[#1a1207]">
          <h2 className="text-2xl font-light text-white mb-2">
            {t("welcome.title")}
            <span className="text-amber-500">.</span>{" "}
            <span className="text-zinc-500 text-base">{user?.name}</span>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">{t("welcome.body")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="new-request-btn" onClick={() => setFormOpen(true)} className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10">
              <Plus size={16} weight="bold" className="mr-1.5" />
              {t("nav.new_request")}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3 mb-6">
          <Statcard count={pendingCount} label={t("rr.status.pending_price")} />
          <Statcard count={pricedCount} label={t("rr.status.priced")} />
          <Statcard count={activeCount} label={t("nav.tracking")} />
        </div>

        {/* Contact card */}
        <div className="mb-6">
          <SDriveContactCard compact />
        </div>

        {/* Phase 26 — dispatch client invoices ("Mes documents") */}
        <div className="mb-6">
          <MyInvoicesCard />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-zinc-800 overflow-x-auto">
          {[
            { key: "requests", label: t("nav.requests") },
            { key: "tracking", label: t("nav.tracking") },
          ].map((x) => (
            <button
              key={x.key}
              data-testid={`agency-tab-${x.key}`}
              onClick={() => setTab(x.key)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === x.key ? "border-amber-500 text-amber-500" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>

        <LoadErrorBanner error={loadError} onRetry={fetchAll} />

        {tab === "requests" ? (
          <div className="space-y-3">
            {requests.length === 0 ? (
              <div data-testid="rr-empty" className="text-center py-14 text-zinc-500">{t("rr.empty")}</div>
            ) : (
              requests.map((r) => (
                <div key={r.id} data-testid={`rr-card-${r.id}`} className="bg-[#121212] border border-zinc-800 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-base font-medium text-white truncate">
                        {r.pickup_location} → {r.dropoff_location}
                      </p>
                      <p className="text-sm text-zinc-400">
                        {fmtDate(r.pickup_date)} — {r.pickup_time} — {r.client_name}
                      </p>
                    </div>
                    <Badge className={`${statusBadgeColor(r.status)} border whitespace-nowrap`}>
                      {t(`rr.status.${r.status}`)}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500 mb-3">
                    <span>{r.passenger_count} pax</span>
                    <span>· {r.luggage_count} {t("form.luggage").toLowerCase()}</span>
                    <span>· {(r.vehicle_type || "eco").toUpperCase()}</span>
                    {r.admin_price != null && (
                      <span className="text-amber-400 font-semibold text-sm">
                        {t("rr.price_quoted")}: {r.admin_price} EUR
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setViewRequest(r)} className="h-8 text-xs text-zinc-400 border border-zinc-800">
                      <Eye size={14} className="mr-1" /> Details
                    </Button>
                    {r.admin_price != null && ["priced", "accepted", "converted"].includes(r.status) && (
                      <Button data-testid={`rr-quote-pdf-${r.id}`} size="sm" variant="ghost" onClick={() => downloadPdf(`/ride-requests/${r.id}/quote-pdf`)} className="h-8 text-xs text-amber-300 border border-amber-500/30 hover:bg-amber-500/10">
                        <FilePdf size={14} className="mr-1" /> Devis (PDF)
                      </Button>
                    )}
                    {r.status === "converted" && r.ride_id && (
                      <Button data-testid={`rr-voucher-pdf-${r.id}`} size="sm" variant="ghost" onClick={() => downloadPdf(`/rides/${r.ride_id}/voucher-pdf`)} className="h-8 text-xs text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/10">
                        <FilePdf size={14} className="mr-1" /> Confirmation
                      </Button>
                    )}
                    {r.status === "priced" && (
                      <>
                        <Button data-testid={`rr-accept-${r.id}`} size="sm" onClick={() => setConfirmAction({ type: "accept", id: r.id })} className="h-8 text-xs bg-emerald-600 text-white hover:bg-emerald-700">
                          <CheckCircle size={14} className="mr-1" /> {t("rr.accept")}
                        </Button>
                        <Button data-testid={`rr-reject-${r.id}`} size="sm" variant="ghost" onClick={() => setConfirmAction({ type: "reject", id: r.id })} className="h-8 text-xs text-red-400 border border-red-500/30">
                          <XCircle size={14} className="mr-1" /> {t("rr.reject")}
                        </Button>
                      </>
                    )}
                    {["pending_price", "rejected", "cancelled"].includes(r.status) && (
                      <Button data-testid={`rr-delete-${r.id}`} size="sm" variant="ghost" onClick={() => setConfirmAction({ type: "delete", id: r.id })} className="h-8 text-xs text-red-400 border border-zinc-800">
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {rides.length === 0 ? (
              <div data-testid="tr-empty" className="text-center py-14 text-zinc-500">{t("tr.empty")}</div>
            ) : (
              rides.map((r) => (
                <div key={r.id} data-testid={`tr-card-${r.id}`} className="bg-[#121212] border border-zinc-800 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-base font-medium text-white">{r.route_label}</p>
                      <p className="text-sm text-zinc-400">
                        {fmtDate(r.pickup_date)} — {r.pickup_time}
                      </p>
                    </div>
                    <Badge className={`${statusBadgeColor(r.status)} border whitespace-nowrap`}>
                      {r.status_label?.[lang] || r.status_label?.en || r.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-xs text-zinc-400">
                    <div className="flex gap-1.5 items-center">
                      <MapPin size={12} className="text-zinc-500" /> {t("tr.pickup")}: <span className="text-zinc-200">{r.pickup_location}</span>
                    </div>
                    <div className="flex gap-1.5 items-center">
                      <MapPin size={12} className="text-zinc-500" /> {t("tr.dropoff")}: <span className="text-zinc-200">{r.dropoff_location}</span>
                    </div>
                    <div>{t("tr.price")}: <span className="text-amber-400 font-semibold">{r.price} EUR</span></div>
                    <div>{t("tr.vehicle")}: <span className="text-zinc-200 uppercase">{r.vehicle_type}</span></div>
                    {r.driver_name && (
                      <>
                        <div className="flex gap-1.5 items-center">
                          <CarProfile size={12} className="text-zinc-500" /> {t("tr.driver")}: <span className="text-zinc-200">{r.driver_name}</span>
                        </div>
                        {r.driver_vehicle && <div>{r.driver_vehicle} {r.driver_plate ? `· ${r.driver_plate}` : ""}</div>}
                        <div className="flex gap-1.5 items-start col-span-full mt-1">
                          <span className="text-zinc-400 mr-1">{t("tr.driver_phone")}:</span>
                          {r.phone_visible && r.driver_phone ? (
                            <PhoneActions phone={r.driver_phone} size="xs" />
                          ) : (
                            <span className="text-zinc-500 inline-flex items-center gap-1">
                              <ClockCountdown size={12} /> {t("tr.phone_hidden")}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-2">Ref: {r.id.slice(0, 8)} · {fmtDateTime(r.created_at)}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button data-testid={`tr-voucher-pdf-${r.id}`} size="sm" variant="ghost" onClick={() => downloadPdf(`/rides/${r.id}/voucher-pdf`)} className="h-8 text-xs text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/10">
                      <FilePdf size={14} className="mr-1" /> Télécharger la confirmation
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      <AgencyRequestForm open={formOpen} onClose={() => setFormOpen(false)} onCreated={() => { fetchAll(); setTimeout(() => setShowRequestReceived(true), 350); }} />

      {/* Request received confirmation */}
      <Dialog open={showRequestReceived} onOpenChange={() => setShowRequestReceived(false)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="request-received-title" className="flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-400" />
              {t("contact.after_submit_title")}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 leading-relaxed pt-2" data-testid="request-received-body">
              {t("contact.after_submit_body")}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <SDriveContactCard compact />
          </div>
          <div className="flex justify-end pt-1">
            <Button data-testid="request-received-close" onClick={() => setShowRequestReceived(false)} className="bg-amber-500 text-black font-semibold hover:bg-amber-600">
              {t("common.close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm action */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "accept" && t("rr.accept")}
              {confirmAction?.type === "reject" && t("rr.reject")}
              {confirmAction?.type === "delete" && t("rr.delete")}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {t("form.cancel")}
            </Button>
            <Button
              data-testid="confirm-action-btn"
              onClick={() => {
                const { type, id } = confirmAction;
                if (type === "accept") acceptRequest(id);
                if (type === "reject") rejectRequest(id);
                if (type === "delete") deleteRequest(id);
                setConfirmAction(null);
              }}
              className="flex-1 bg-amber-500 text-black hover:bg-amber-600 font-semibold"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request detail */}
      <Dialog open={!!viewRequest} onOpenChange={() => setViewRequest(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{viewRequest?.pickup_location} → {viewRequest?.dropoff_location}</DialogTitle>
            <DialogDescription className="text-zinc-500">Ref: {viewRequest?.id?.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          {viewRequest && (
            <div className="space-y-2 text-sm">
              <Row label={t("form.client_name")} value={viewRequest.client_name} />
              <Row label={t("form.client_phone")} value={viewRequest.client_phone} />
              <Row label={t("form.pickup_date")} value={fmtDate(viewRequest.pickup_date)} />
              <Row label={t("form.pickup_time")} value={viewRequest.pickup_time} />
              <Row label={t("form.passengers")} value={viewRequest.passenger_count} />
              <Row label={t("form.luggage")} value={viewRequest.luggage_count} />
              <Row label={t("form.vehicle")} value={(viewRequest.vehicle_type || "").toUpperCase()} />
              {viewRequest.child_equipment && <Row label={t("form.child_eq")} value={viewRequest.child_equipment} />}
              {viewRequest.notes && <Row label={t("form.notes")} value={viewRequest.notes} />}
              {viewRequest.admin_price != null && (
                <>
                  <Row label={t("rr.price_quoted")} value={`${viewRequest.admin_price} EUR`} className="text-amber-400 font-semibold" />
                  <PriceEstimateNote price={viewRequest.admin_price} lang={lang} />
                </>
              )}
              <p className="text-xs text-zinc-500 pt-2">{t("rr.created_on")} {fmtDateTime(viewRequest.created_at)}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, className = "" }) {
  return (
    <div className="flex justify-between border-b border-zinc-800/50 pb-1.5">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      <span className={`text-zinc-100 ${className}`}>{value}</span>
    </div>
  );
}
